import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import API from '@/lib/api';
import { Scale, AlertTriangle, Send, CheckCircle, RotateCcw, ShieldQuestion } from 'lucide-react';

const STATUS_META = {
  open: { label: 'In mediation', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: Scale },
  resolved_release: { label: 'Resolved — funds released to creator', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  resolved_refund: { label: 'Resolved — refunded to agency', cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: RotateCcw },
};

export function MediationBox({ dealId, role, fundsHeld, onChange }) {
  const [caseData, setCaseData] = useState(undefined); // undefined=loading, null=none
  const [showForm, setShowForm] = useState(false);
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const isAgency = role === 'agency';

  const fetchCase = useCallback(async () => {
    try {
      const res = await API.get(`/mediation/deals/${dealId}/case`);
      setCaseData(res.data.case);
    } catch { setCaseData(null); }
  }, [dealId]);

  useEffect(() => { fetchCase(); }, [fetchCase]);

  const submitRequest = async () => {
    if (!reason.trim()) { toast.error('Please add a reason'); return; }
    setBusy(true);
    try {
      const url = isAgency ? `/buyer/deals/${dealId}/refund-request` : `/creator/deals/${dealId}/release-request`;
      await API.post(url, { reason });
      toast.success(isAgency ? 'Refund request submitted' : 'Release request submitted');
      setShowForm(false); setReason('');
      await fetchCase();
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const sendMessage = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await API.post(`/mediation/cases/${caseData.id}/messages`, { message: msg });
      setMsg('');
      await fetchCase();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  if (caseData === undefined) return null;

  // No case yet
  if (!caseData) {
    if (!fundsHeld) return null;
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="mediation-box">
        {!showForm ? (
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2.5">
              <ShieldQuestion className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-700">
                  {isAgency ? 'Not happy with the outcome?' : 'Think the agency overstepped the agreed terms?'}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {isAgency
                    ? "If the creator hasn't approved content within the agreed window, you can request a refund. Our team will mediate."
                    : 'If the content submission exceeded or fell short of the agreed terms, you can request release. Our team will mediate.'}
                </p>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowForm(true)} data-testid="mediation-request-btn"
              className="shrink-0 text-amber-700 border-amber-200 hover:bg-amber-50 text-xs h-8">
              <AlertTriangle className="w-3.5 h-3.5 mr-1" />
              {isAgency ? 'Request refund' : 'Request release'}
            </Button>
          </div>
        ) : (
          <div className="space-y-2" data-testid="mediation-form">
            <p className="text-sm font-medium text-slate-700">
              {isAgency ? 'Why are you requesting a refund?' : 'How did the submission breach the agreed terms?'}
            </p>
            <Textarea data-testid="mediation-reason-input" value={reason} onChange={e => setReason(e.target.value)}
              rows={3} placeholder="Give as much detail as you can — this helps us resolve it faster." className="text-sm border-slate-200" />
            <div className="flex gap-2">
              <Button size="sm" onClick={submitRequest} disabled={busy} data-testid="mediation-submit-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white text-xs h-8">Submit to mediation</Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowForm(false); setReason(''); }} className="text-slate-400 text-xs h-8">Cancel</Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Case exists
  const meta = STATUS_META[caseData.status] || STATUS_META.open;
  const Icon = meta.icon;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="mediation-box">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Scale className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-800">
            {caseData.type === 'agency_refund_request' ? 'Refund request' : 'Release request'}
          </span>
        </div>
        <Badge variant="outline" className={`text-[10px] ${meta.cls}`} data-testid="mediation-status">
          <Icon className="w-3 h-3 mr-1" />{meta.label}
        </Badge>
      </div>

      <div className="space-y-2 max-h-56 overflow-auto pr-1" data-testid="mediation-thread">
        {(caseData.messages || []).map((m, i) => (
          <div key={i} className={`text-xs rounded-lg p-2.5 ${m.by_role === 'admin' ? 'bg-violet-50 border border-violet-100' : m.by_role === 'agency' ? 'bg-blue-50' : 'bg-slate-50'}`}>
            <span className="font-semibold capitalize text-slate-600">{m.by_role === 'admin' ? 'Mediator' : m.by_role}</span>
            <p className="text-slate-700 mt-0.5 whitespace-pre-wrap">{m.message}</p>
          </div>
        ))}
      </div>

      {caseData.resolution?.note && (
        <div className="mt-2 text-xs rounded-lg p-2.5 bg-violet-50 border border-violet-100">
          <span className="font-semibold text-violet-700">Mediator decision</span>
          <p className="text-slate-700 mt-0.5">{caseData.resolution.note}</p>
        </div>
      )}

      {caseData.status === 'open' && (
        <div className="flex gap-2 mt-3">
          <Textarea data-testid="mediation-message-input" value={msg} onChange={e => setMsg(e.target.value)}
            rows={1} placeholder="Add a comment for the mediator…" className="text-xs border-slate-200 resize-none" />
          <Button size="sm" onClick={sendMessage} disabled={busy} data-testid="mediation-send-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white h-auto px-3"><Send className="w-3.5 h-3.5" /></Button>
        </div>
      )}
    </div>
  );
}
